/**
 * Modelo EPANET (.inp) gerado da rede do cadastro, para a simulação no navegador — Optimale Grid (US-405).
 *
 * Criado em: 16/10/2026 · Última modificação: 16/10/2026 · Versão: 1.0.0
 *
 * Mesmo formato do importador da prova S1:
 * - nós com cota do relevo e demanda pelas casas (200 L/hab/dia × 3,5 hab por ligação, padrão de consumo de 24 h);
 * - trechos com DN e rugosidade do cadastro;
 * - reservatórios do cadastro como carga fixa (cota + lâmina de 6 m, ou 18 m para elevado), ligados ao nó mais próximo.
 * Fica só nos componentes com reservatório (o resto da rede não tem de onde receber água).
 * A simulação no SERVIDOR, com a checagem obrigatória da NBR 12218 antes da manobra, é a GUS-1105.
 */
import type { Rede } from "./rede";
import { marcaAtual } from "../marca";

const CONSUMO_POR_LIGACAO_LPS = 0.0081;
const PADRAO = [0.5, 0.45, 0.4, 0.4, 0.45, 0.6, 0.9, 1.3, 1.4, 1.3, 1.2, 1.2, 1.3, 1.2, 1.1, 1.05, 1.1, 1.25, 1.45, 1.5, 1.3, 1.0, 0.8, 0.6];

export function gerarInp(rede: Rede, cota: (x: number, z: number) => number): string {
  const nos = [...rede.nos.values()];
  // componentes conexas (union-find) para manter só a parte abastecida por reservatório
  const indice = new Map(nos.map((n, i) => [n.id, i]));
  const pai = nos.map((_, i) => i);
  const raiz = (i: number): number => { while (pai[i] !== i) { pai[i] = pai[pai[i]]; i = pai[i]; } return i; };
  rede.trechos.forEach((t) => {
    const a = indice.get(t.de), b = indice.get(t.para);
    if (a !== undefined && b !== undefined) pai[raiz(a)] = raiz(b);
  });
  const reservatorios = rede.equipamentos.filter((e) => e.tipo === "reservatorio" && e.id);
  const ligacoes = reservatorios.map((r) => {
    let melhor = nos[0], d2 = Infinity;
    for (const n of nos) {
      const d = (n.x - r.x) ** 2 + (n.z - r.z) ** 2;
      if (d < d2) { d2 = d; melhor = n; }
    }
    return { r, no: melhor, distancia: Math.max(1, Math.sqrt(d2)) };
  }).filter((l) => l.distancia < 300);
  const abastecidos = new Set(ligacoes.map((l) => raiz(indice.get(l.no.id)!)));
  const manter = new Set(nos.filter((n) => abastecidos.has(raiz(indice.get(n.id)!))).map((n) => n.id));

  const linhas = ["[TITLE]", `Rede do cadastro (${marcaAtual().produto}) — simulação na tela`, "", "[JUNCTIONS]", ";ID  Cota  Demanda  Padrao"];
  nos.filter((n) => manter.has(n.id)).forEach((n) =>
    linhas.push(`${n.id}  ${n.cota.toFixed(2)}  ${(n.casas * CONSUMO_POR_LIGACAO_LPS).toFixed(4)}  CONSUMO  ;${n.casas}`));
  linhas.push("", "[RESERVOIRS]", ";ID  Carga");
  ligacoes.forEach(({ r }) => {
    const elevado = r.id.startsWith("REL");
    linhas.push(`${r.id}  ${(cota(r.x, r.z) + (elevado ? 18 : 6)).toFixed(2)}  ;${r.detalhe}`);
  });
  linhas.push("", "[PIPES]", ";ID  No1  No2  Comprimento  Diametro  Rugosidade  PerdaLocal  Estado");
  rede.trechos.filter((t) => manter.has(t.de) && manter.has(t.para)).forEach((t) =>
    linhas.push(`${t.id}  ${t.de}  ${t.para}  ${Math.max(0.5, t.comprimento).toFixed(1)}  ${t.dn}  ${t.rugosidade ?? 140}  0  Open  ;${t.setor}`));
  ligacoes.forEach(({ r, no, distancia }) => linhas.push(`L_${r.id}  ${r.id}  ${no.id}  ${distancia.toFixed(1)}  150  140  0  Open`));
  linhas.push("", "[PATTERNS]", `CONSUMO  ${PADRAO.join("  ")}`, "", "[TIMES]", "Duration  24:00", "Hydraulic Timestep  1:00",
    "Pattern Timestep  1:00", "", "[OPTIONS]", "Units  LPS", "Headloss  H-W", "", "[COORDINATES]");
  nos.filter((n) => manter.has(n.id)).forEach((n) => linhas.push(`${n.id}  ${n.lon.toFixed(7)}  ${n.lat.toFixed(7)}`));
  ligacoes.forEach(({ r }) => linhas.push(`${r.id}  ${r.lon.toFixed(7)}  ${r.lat.toFixed(7)}`));
  linhas.push("", "[END]", "");
  return linhas.join("\n");
}
